import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, AlertTriangle, AlertCircle, Info } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { productionLogger } from "@/utils/production-logger";
import { TIMING } from '@/constants';

interface SecurityMetrics {
  alerts: {
    total_active: number;
    critical_count: number;
    high_count: number;
    medium_count: number;
    recent_24h: number;
  };
  security_events: {
    total_24h: number;
  };
}

export function SecurityStatsWidget() {
  const [metrics, setMetrics] = useState<SecurityMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const fetchSecurityMetrics = async () => {
      try {
        // Enhanced Security: Check rate limiting before proceeding
        const { data: rateLimitCheck, error: rateLimitError } = await supabase
          .rpc('check_admin_dashboard_rate_limit');
          
        if (rateLimitError) {
          throw new Error(`Rate limit check failed: ${rateLimitError.message}`);
        }
        
        if (!rateLimitCheck) {
          throw new Error('Rate limit exceeded for security widget access');
        }

        // Enhanced Security: Log widget access for audit trail
        const { error: logError } = await supabase
          .rpc('log_dashboard_widget_access', {
            p_widget_type: 'security_stats',
            p_data_points: 1
          });
          
        if (logError) {
          productionLogger.warn('Failed to log widget access:', logError);
          // Continue despite logging failure - don't block widget functionality
        }

        // Call the secure function that logs widget access
        const { data, error } = await supabase.rpc('get_security_dashboard_metrics');
        
        if (error) throw error;
        
        if (mounted) {
          setMetrics(data as unknown as SecurityMetrics);
          setError(null);
        }
      } catch (err) {
        productionLogger.error('Failed to fetch security metrics:', err);
        if (mounted) {
          if (err instanceof Error && err.message.includes('Rate limit exceeded')) {
            setError('Rate limit exceeded - please wait before refreshing');
          } else if (err instanceof Error && err.message.includes('Rate limit check failed')) {
            setError('Security check failed - contact administrator');
          } else {
            setError('Unable to load security data');
          }
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchSecurityMetrics();

    // Enhanced Security: Set up real-time subscription with RLS channel filtering
    const channel = supabase
      .channel('security_stats_widget_enhanced', {
        config: {
          broadcast: { self: true },
          presence: { key: 'security_admin' }
        }
      })
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'security_alerts',
          filter: 'resolved=eq.false' // Only listen to active alerts
        },
        async (payload) => {
          productionLogger.info('Security alert real-time update:', payload);
          // Enhanced: Add debounced refresh to prevent rate limit issues
          setTimeout(() => {
            if (mounted) {
              fetchSecurityMetrics();
            }
          }, 1000);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'security_events',
          filter: 'severity=in.(high,critical)' // Only high priority events
        },
        async (payload) => {
          productionLogger.info('Critical security event:', payload);
          // Enhanced: Immediate refresh for critical events
          if (mounted) {
            fetchSecurityMetrics();
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          productionLogger.info('Security widget real-time subscription active');
        } else if (status === 'CHANNEL_ERROR') {
          productionLogger.error('Security widget subscription error');
        }
      });

    // Enhanced Security: Reduced refresh to every 60 seconds to respect rate limits
    const interval = setInterval(fetchSecurityMetrics, TIMING.ADMIN_WIDGET_REFRESH);

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Security Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-8 bg-muted rounded w-16"></div>
            <div className="h-4 bg-muted rounded w-24"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Shield className="w-4 h-4 text-destructive" />
            Security Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-destructive text-sm">{error}</div>
        </CardContent>
      </Card>
    );
  }

  const criticalCount = metrics?.alerts?.critical_count || 0;
  const highCount = metrics?.alerts?.high_count || 0;
  const totalActive = metrics?.alerts?.total_active || 0;
  const recentEvents = metrics?.security_events?.total_24h || 0;

  const severity = criticalCount > 0 ? 'critical' : highCount > 0 ? 'high' : 'normal';
  const statusColor = severity === 'critical' ? 'text-destructive' : 
                     severity === 'high' ? 'text-accent' : 'text-primary';

  return (
    <Card className="min-h-[140px]">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Shield className={`w-4 h-4 ${statusColor}`} />
          Security Status
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Shield className={`w-6 h-6 ${statusColor}`} />
            <div className={`text-2xl font-bold ${statusColor}`}>
              {totalActive}
            </div>
            {criticalCount > 0 && (
              <AlertCircle className="w-4 h-4 text-destructive ml-2" />
            )}
            {highCount > 0 && (
              <AlertTriangle className="w-4 h-4 text-accent ml-1" />
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {totalActive === 0 ? "All systems secure" : `${criticalCount + highCount} priority alerts`}
          </div>
          <div className="flex justify-between text-xs border-t pt-2">
            <span className="text-muted-foreground">24h events</span>
            <span className="font-medium">{recentEvents}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}